import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { ShareModal } from '../components/ShareModal';
import { useConfirm } from '../components/ConfirmProvider';
import { getSite, listRams, archiveRams, type Site, type RamsItem } from '../lib/db';
import { cacheGet, cacheListByOrg } from '../lib/cache';
import { getSignedUrl } from '../lib/storage';
import { useUserOrg } from '../lib/org';
import { supabase } from '../lib/supabase';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * RAMS list view — shows every RAMS for a site, grouped by company, with
 * Open / Email / Remove actions on each row.
 *
 * Lives on its own page (route: /sites/:siteId/rams/list) so the "add new"
 * menu at /sites/:siteId/rams can stay clean. Reached via the "These are
 * the RAMS for your site" banner on the action menu, or by being redirected
 * here from RamsPresetForm / RamsAdd after saving (with ?share=<id> to
 * auto-open the share modal).
 */

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function RamsListView() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [site, setSite] = useState<Site | null>(null);
  const [items, setItems] = useState<RamsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [shareTarget, setShareTarget] = useState<RamsItem | null>(null);
  const [shareJustCreated, setShareJustCreated] = useState(false);
  const [senderName, setSenderName] = useState<string>('');

  async function refresh() {
    if (!siteId) return;
    setLoading(true);
    try {
      const [s, list] = await Promise.all([getSite(siteId), listRams(siteId)]);
      setSite(s);
      setItems(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load — stale-while-revalidate. Post-mutation refreshes
  // (after share / archive) still call refresh() directly which uses
  // network-only behaviour.
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    let haveCache = false;

    // Phase 1: cache-first paint.
    (async () => {
      const cachedSite = await cacheGet<Site>('sites', siteId);
      if (cancelled || !cachedSite) return;
      const allRams = await cacheListByOrg<RamsItem>('rams', cachedSite.org_id);
      if (cancelled) return;
      haveCache = true;
      setSite(cachedSite);
      setItems(allRams.filter((r) => r.site_id === siteId));
      setLoading(false);
    })().catch(() => {});

    // Phase 2: fresh network fetch (inline to avoid refresh()'s
    // setLoading(true) flipping us back to a loading state).
    Promise.all([getSite(siteId), listRams(siteId)])
      .then(([s, list]) => {
        if (cancelled) return;
        setSite(s);
        setItems(list);
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });

    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [siteId]);

  // Auto-open share modal when redirected here after creating a RAMS.
  useEffect(() => {
    const shareId = searchParams.get('share');
    if (!shareId || items.length === 0) return;
    const target = items.find((r) => r.id === shareId);
    if (target) {
      setShareTarget(target);
      setShareJustCreated(true);
      const next = new URLSearchParams(searchParams);
      next.delete('share');
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  // Sender's name for the "Sent by" line in emails.
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      setSenderName(name);
    });
  }, []);

  async function openRams(r: RamsItem) {
    setOpening(r.id);
    try {
      const url = await getSignedUrl('rams-files', r.file_url, 3600);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(null);
    }
  }

  function openShareModalFor(r: RamsItem) {
    setShareJustCreated(false);
    setShareTarget(r);
  }

  function onEdit(r: RamsItem) {
    // Route back to the same template editor with ?ramsId so the form
    // hydrates from the row's saved form_state and saves in update
    // mode. We try the stored form_state.templateId first (only present
    // on RAMS created from this commit onward); fall back to trade_preset
    // → template lookup; final fallback is "blank" so old rows still
    // open into something editable, just from blank rather than the
    // exact template they came from.
    let tplId = 'blank';
    const fs = (r.form_state ?? null) as Record<string, unknown> | null;
    if (fs && typeof fs.templateId === 'string') {
      tplId = fs.templateId;
    } else if (r.trade) {
      // Find any template whose primary trade matches the row's trade.
      // Best-effort — the user can re-pick if they don't like the match.
      // (Not importing RAMS_TEMPLATES here to keep the page lean — we
      // hand the trade slug through and the editor resolves it.)
      tplId = r.trade.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    }
    nav(`/sites/${r.site_id}/rams/preset/${tplId}?ramsId=${r.id}`);
  }

  async function onArchive(r: RamsItem) {
    if (!(await confirm({
      title: `Remove "${r.title}" from RAMS library?`,
      body: 'The file stays in storage but the entry is hidden.',
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    try {
      await archiveRams(r.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (loading) {
    return <AppShell title="RAMS for this site" back={`/sites/${siteId ?? ''}/rams`}><LoadingIndicator /></AppShell>;
  }
  if (!site) {
    return (
      <AppShell title="RAMS for this site" back={`/sites/${siteId ?? ''}/rams`}>
        <div className="banner banner-error">{error ?? 'Site not found.'}</div>
        <button type="button" className="btn btn-ghost" onClick={refresh} style={{ marginTop: 12 }}>
          Try again
        </button>
      </AppShell>
    );
  }

  // Group by company (case-insensitive)
  const groups: Record<string, RamsItem[]> = {};
  for (const it of items) {
    const key = (it.company ?? '').trim() || '(no company specified)';
    (groups[key] = groups[key] ?? []).push(it);
  }
  const companyNames = Object.keys(groups).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );

  return (
    <AppShell title="RAMS for this site" back={`/sites/${site.id}/rams`}>
      {error && (
        <div className="banner banner-error" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span>{error}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setError(null); refresh(); }}>
            Try again
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', marginTop: 12 }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No RAMS yet. Tap "‹ Back" to add one from the menu.
          </p>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
              <strong>{items.length}</strong> on file for <strong>{site.name}</strong> · grouped by company.
              Tap a row to open the PDF, the <strong>📨 Email</strong> button to send a 30-day download link.
            </p>
          </div>

          {companyNames.map((c) => (
            <div key={c} style={{ marginBottom: 16 }}>
              <h3 className="rams-company-h">
                {c} <span className="rams-company-count">({groups[c].length})</span>
              </h3>
              <ul className="rams-list">
                {groups[c].map((r) => (
                  <li key={r.id} className="rams-row">
                    <button
                      type="button"
                      className="rams-row-tap"
                      onClick={() => openRams(r)}
                      disabled={opening === r.id}
                    >
                      <div className="rams-row-text">
                        <p className="rams-title">{r.title}</p>
                        <p className="rams-meta">
                          {[r.trade, fmtDate(r.created_at), fmtSize(r.size_bytes)].filter(Boolean).join(' · ')}
                        </p>
                        {r.hazard_presets.length > 0 && (
                          <div className="rams-hazards">
                            {r.hazard_presets.slice(0, 4).map((h) => (
                              <span key={h} className="rams-hazard-chip">{h}</span>
                            ))}
                            {r.hazard_presets.length > 4 && (
                              <span className="rams-hazard-chip">+{r.hazard_presets.length - 4}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="chev">{opening === r.id ? '…' : '↗'}</span>
                    </button>
                    <div className="rams-row-actions">
                      <button
                        type="button"
                        className="btn-tiny"
                        onClick={() => onEdit(r)}
                        aria-label={`Edit ${r.title}`}
                        title="Re-open this RAMS in the editor and save a revised version"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-tiny"
                        onClick={() => openShareModalFor(r)}
                        aria-label={`Email ${r.title}`}
                        title="Email this RAMS — sends a branded email with a 30-day download link"
                      >
                        📨 Email
                      </button>
                      <button
                        type="button"
                        className="btn-tiny"
                        onClick={() => onArchive(r)}
                        aria-label={`Remove ${r.title}`}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
        <Link to={`/sites/${site.id}/rams`} className="btn btn-secondary" style={{ flex: 1 }}>
          ‹ Back to RAMS menu
        </Link>
        <button
          type="button"
          className="btn btn-primary"
          style={{ flex: 1 }}
          onClick={() => nav(`/sites/${site.id}/rams`)}
        >
          + Add another
        </button>
      </div>

      {shareTarget && (
        <ShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          docTitle={shareTarget.title}
          docType="RAMS"
          orgName={org?.org_name ?? undefined}
          siteName={site.name}
          senderName={senderName || undefined}
          introCopy={
            shareJustCreated
              ? 'RAMS saved. Send a copy to anyone who needs it — the subbie doing the work, the main contractor, anyone signing it off.'
              : undefined
          }
          getShareUrl={() =>
            getSignedUrl('rams-files', shareTarget.file_url, 30 * 24 * 60 * 60)
          }
        />
      )}
    </AppShell>
  );
}
